import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Loader2, ShieldOff, Coins, Gauge, Hash } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardBody } from "@/components/ui/card";
import { StatCard } from "@/components/ui/StatCard";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";

/** Dữ liệu GET /api/ai/usage — khớp AiUsageService.summary() (apps/api). */
type UsageSummary = {
  totalCalls: number;
  totalTokens: number;
  totalCostUsd: number;
  byFeature: { feature: string; calls: number; costUsd: number }[];
};

/** Một dòng của GET /api/ai/usage/log — khớp AiUsageService.logEntries(). */
type UsageLogEntry = {
  id: number;
  createdDate: string;
  staffId: number | null;
  loginName: string | null;
  feature: string;
  model: string | null;
  textId: number | null;
  attachId: number | null;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  latencyMs: number;
  status: number;
  errorMessage: string | null;
};
type UsageLogPage = { items: UsageLogEntry[]; total: number };

const FEATURE_LABEL: Record<string, string> = {
  spellcheck: "Kiểm tra thể thức",
  summary: "Tóm tắt văn bản",
};

function featureLabel(f: string): string {
  return FEATURE_LABEL[f] ?? f;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

type ByFeatureRow = { feature: string; calls: number; costUsd: number };

/**
 * Màn quản trị "Theo dõi chi phí AI" — CHỈ ADMIN gọi được:
 * - `GET /api/ai/usage` — số liệu tổng hợp theo khoảng ngày, tách theo tính năng.
 * - `GET /api/ai/usage/log` — nhật ký CHI TIẾT từng lượt gọi (bổ sung sau khi
 *   nhận ra bảng tổng hợp trả lời "tiêu bao nhiêu" nhưng không trả lời "vì
 *   sao": ai gọi, văn bản nào, model nào, lượt nào lỗi).
 */
export function AiUsagePage() {
  const [fromDate, setFromDate] = useState(daysAgoStr(6));
  const [toDate, setToDate] = useState(todayStr());
  const [appliedFrom, setAppliedFrom] = useState(daysAgoStr(6));
  const [appliedTo, setAppliedTo] = useState(todayStr());

  // Bảng tổng hợp theo tính năng — mảng nhỏ (mỗi tính năng một dòng), phân
  // trang phía client cho đúng khuôn dùng lại Pagination.
  const [featurePage, setFeaturePage] = useState(1);
  const [featurePageSize, setFeaturePageSize] = useState(DEFAULT_PAGE_SIZE);

  // Bảng nhật ký chi tiết — phân trang phía MÁY CHỦ (GET /ai/usage/log).
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["ai-usage", appliedFrom, appliedTo],
    queryFn: async () => {
      const res = await api.get("/ai/usage", {
        params: { fromDate: appliedFrom, toDate: appliedTo },
      });
      return (res.data?.data ?? res.data) as UsageSummary;
    },
    retry: false,
  });

  const {
    data: logData,
    isLoading: logLoading,
    isError: logIsError,
    error: logError,
  } = useQuery({
    queryKey: ["ai-usage-log", appliedFrom, appliedTo, logPage, logPageSize],
    queryFn: async () => {
      const res = await api.get("/ai/usage/log", {
        params: { fromDate: appliedFrom, toDate: appliedTo, page: logPage, pageSize: logPageSize },
      });
      return (res.data?.data ?? res.data) as UsageLogPage;
    },
    retry: false,
  });

  const forbidden =
    (error as AxiosError | undefined)?.response?.status === 403 ||
    (logError as AxiosError | undefined)?.response?.status === 403;

  const featureRows: ByFeatureRow[] = data?.byFeature ?? [];
  const pagedFeatureRows = featureRows.slice(
    (featurePage - 1) * featurePageSize,
    featurePage * featurePageSize,
  );

  const featureColumns: Column<ByFeatureRow>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">
          {(featurePage - 1) * featurePageSize + i + 1}
        </span>
      ),
    },
    {
      key: "feature",
      header: "Tính năng",
      render: (r) => <span className="font-medium">{featureLabel(r.feature)}</span>,
    },
    {
      key: "calls",
      header: "Số lượt gọi",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.calls.toLocaleString("vi-VN")}</span>,
    },
    {
      key: "cost",
      header: "Chi phí (USD)",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.costUsd.toFixed(4)}</span>,
    },
  ];

  const logColumns: Column<UsageLogEntry>[] = [
    {
      key: "time",
      header: "Thời điểm",
      width: "160px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.createdDate?.replace("T", " ") ?? "—"}</span>
      ),
    },
    {
      key: "user",
      header: "Người dùng",
      width: "160px",
      render: (r) => (
        <span className="text-sm">{r.loginName ?? (r.staffId ? `#${r.staffId}` : "Hệ thống")}</span>
      ),
    },
    {
      key: "feature",
      header: "Tính năng",
      width: "150px",
      render: (r) => <span>{featureLabel(r.feature)}</span>,
    },
    {
      key: "model",
      header: "Model",
      width: "150px",
      render: (r) => <span className="font-mono text-xs">{r.model ?? "—"}</span>,
    },
    {
      key: "tokens",
      header: "Token vào/ra",
      width: "130px",
      align: "right",
      render: (r) => (
        <span className="tabular-nums text-xs">
          {r.promptTokens.toLocaleString("vi-VN")} / {r.completionTokens.toLocaleString("vi-VN")}
        </span>
      ),
    },
    {
      key: "cost",
      header: "Chi phí (USD)",
      width: "110px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.costUsd.toFixed(5)}</span>,
    },
    {
      key: "latency",
      header: "Phản hồi",
      width: "90px",
      align: "right",
      render: (r) => (
        <span className="tabular-nums text-xs">{r.latencyMs.toLocaleString("vi-VN")} ms</span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "110px",
      render: (r) =>
        r.status === 1 ? (
          <StatusBadge variant="success">Thành công</StatusBadge>
        ) : (
          <span title={r.errorMessage ?? undefined}>
            <StatusBadge variant="danger">Lỗi</StatusBadge>
          </span>
        ),
    },
  ];

  if (forbidden) {
    return (
      <div>
        <PageHeader title="Theo dõi chi phí AI" breadcrumb="Quản trị › Chi phí AI" />
        <Card>
          <CardBody className="py-16 text-center">
            <div className="inline-grid place-items-center h-14 w-14 rounded-full bg-destructive/10 text-destructive mb-4">
              <ShieldOff className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="font-serif italic text-2xl text-foreground mb-2">
              Bạn không có quyền
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Chỉ quản trị viên (ADMIN) mới xem được thống kê chi phí AI.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Theo dõi chi phí AI"
        breadcrumb="Quản trị › Chi phí AI"
        subtitle={`Từ ${appliedFrom} đến ${appliedTo}`}
      />

      <SearchPanel
        title="Khoảng ngày"
        fields={[
          {
            label: "Từ ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            ),
          },
          {
            label: "Đến ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            ),
          },
        ]}
        onSubmit={() => {
          setAppliedFrom(fromDate);
          setAppliedTo(toDate);
          setFeaturePage(1);
          setLogPage(1);
        }}
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Đang tải số liệu…
        </div>
      )}

      {!isLoading && isError && !forbidden && (
        <div className="text-sm text-muted-foreground py-10 text-center">
          Không tải được số liệu chi phí AI. Vui lòng thử lại sau.
        </div>
      )}

      {!isLoading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <StatCard label="Tổng lượt gọi" value={data.totalCalls} icon={Hash} tone="primary" />
            <StatCard label="Tổng token" value={data.totalTokens} icon={Gauge} tone="accent" />
            <StatCard
              label="Tổng chi phí (USD)"
              value={data.totalCostUsd.toFixed(4)}
              icon={Coins}
              tone="warning"
            />
          </div>

          <Card className="mb-4">
            <CardBody className="p-0">
              <div className="px-4 sm:px-5 py-3 border-b border-border">
                <div className="font-semibold text-sm uppercase tracking-wider">
                  Tổng hợp theo tính năng
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <DataTable
                  columns={featureColumns}
                  rows={pagedFeatureRows}
                  keyBy={(r) => r.feature}
                  dense
                  empty="Không có dữ liệu sử dụng trong khoảng ngày đã chọn"
                />
                <Pagination
                  page={featurePage}
                  pageSize={featurePageSize}
                  total={featureRows.length}
                  onPageChange={setFeaturePage}
                  onPageSizeChange={(n) => {
                    setFeaturePageSize(n);
                    setFeaturePage(1);
                  }}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="p-0">
              <div className="px-4 sm:px-5 py-3 border-b border-border">
                <div className="font-semibold text-sm uppercase tracking-wider">
                  Nhật ký chi tiết
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Từng lượt gọi AI trong khoảng ngày đã chọn — dùng để tra vì sao chi phí tăng bất
                  thường (ai gọi, văn bản nào, model nào, lượt nào lỗi).
                </p>
              </div>
              <div className="p-4 sm:p-5">
                {logLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" /> Đang tải nhật ký…
                  </div>
                )}
                {!logLoading && logIsError && !forbidden && (
                  <div className="text-sm text-muted-foreground py-6 text-center">
                    Không tải được nhật ký chi tiết. Vui lòng thử lại sau.
                  </div>
                )}
                {!logLoading && logData && (
                  <>
                    <DataTable
                      columns={logColumns}
                      rows={logData.items}
                      keyBy={(r) => r.id}
                      dense
                      empty="Không có lượt gọi nào trong khoảng ngày đã chọn"
                    />
                    <Pagination
                      page={logPage}
                      pageSize={logPageSize}
                      total={logData.total}
                      onPageChange={setLogPage}
                      onPageSizeChange={(n) => {
                        setLogPageSize(n);
                        setLogPage(1);
                      }}
                    />
                  </>
                )}
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
